import type { IconName } from "../components/Icon.js";
import type { TranslationKey } from "../i18n/translations.js";

/**
 * Deliberately local, non-editorial Learn records. These records identify
 * research topics only; they do not contain, activate, or imply guidance.
 * They are bundled with the app shell so the Learn index is available without
 * an active signed safety-content bundle or a network request.
 */
export type LearnResearchCategory = "hazards" | "health" | "preparedness" | "public";
export type LearnResearchStatus = "research_only" | "draft" | "review_required";

export interface LearnResearchSource {
  readonly label: string;
  readonly url: string;
}

export interface LearnResearchMetadata {
  readonly slug: string;
  readonly category: LearnResearchCategory;
  readonly icon: IconName;
  readonly title: TranslationKey;
  readonly detail: TranslationKey;
  readonly status: LearnResearchStatus;
  readonly source: TranslationKey;
  readonly researchedOn: string;
  readonly explanation: TranslationKey;
  readonly whyItMatters: TranslationKey;
  readonly scope: TranslationKey;
  readonly gaps: TranslationKey;
  readonly sources: readonly LearnResearchSource[];
}

export const learnResearchMetadata: readonly LearnResearchMetadata[] = [
  {
    slug: "earthquake",
    category: "hazards",
    icon: "alert",
    title: "learn.library.earthquake.title",
    detail: "learn.library.earthquake.detail",
    status: "review_required",
    source: "learn.library.source.thailand_dossier",
    researchedOn: "2026-09-30",
    explanation: "learn.detail.earthquake.explanation",
    whyItMatters: "learn.detail.earthquake.why",
    scope: "learn.detail.earthquake.scope",
    gaps: "learn.detail.earthquake.gaps",
    sources: [
      {
        label: "DDPM: Earthquake public-safety guidance (2026)",
        url: "https://backofficeminisite.disaster.go.th/apiv1/apps/minisite_ckg/203/content/4848/download?filename=21928eda50b8057eb6708e3c4d52faed.pdf",
      },
      {
        label: "DDPM public disaster-preparedness handbook",
        url: "https://www.disaster.go.th/upload/download/file_attach/58cb60f5607de.pdf",
      },
      {
        label: "USGS: What should I do during an earthquake?",
        url: "https://www.usgs.gov/faqs/what-should-i-do-during-earthquake",
      },
    ],
  },
  {
    slug: "tsunami",
    category: "hazards",
    icon: "shield",
    title: "learn.library.tsunami.title",
    detail: "learn.library.tsunami.detail",
    status: "review_required",
    source: "learn.library.source.thailand_dossier",
    researchedOn: "2026-09-30",
    explanation: "learn.detail.tsunami.explanation",
    whyItMatters: "learn.detail.tsunami.why",
    scope: "learn.detail.tsunami.scope",
    gaps: "learn.detail.tsunami.gaps",
    sources: [
      {
        label: "Thai Meteorological Department: Fundamental Seismology, §7.2",
        url: "https://earthquake.tmd.go.th/documents/file/seismo-doc-1606435108.pdf",
      },
      {
        label: "Thai Meteorological Department: tsunami measures",
        url: "https://www5.tmd.go.th/info/%E0%B8%A1%E0%B8%B2%E0%B8%95%E0%B8%A3%E0%B8%81%E0%B8%B2%E0%B8%A3%E0%B8%9B%E0%B8%AD%E0%B8%87%E0%B8%81%E0%B8%99%E0%B8%A0%E0%B8%A2%E0%B8%88%E0%B8%B2%E0%B8%81%E0%B8%84%E0%B8%A5%E0%B8%99%E0%B8%AA%E0%B8%99%E0%B8%B2%E0%B8%A1",
      },
      { label: "NOAA: Tsunami FAQs", url: "https://www.tsunami.gov/?page=tsunamiFAQ" },
    ],
  },
  {
    slug: "flood-storm",
    category: "hazards",
    icon: "alert",
    title: "learn.library.flood.title",
    detail: "learn.library.flood.detail",
    status: "review_required",
    source: "learn.library.source.thailand_dossier",
    researchedOn: "2026-09-30",
    explanation: "learn.detail.flood.explanation",
    whyItMatters: "learn.detail.flood.why",
    scope: "learn.detail.flood.scope",
    gaps: "learn.detail.flood.gaps",
    sources: [
      {
        label: "DDPM public disaster-preparedness handbook",
        url: "https://www.disaster.go.th/upload/download/file_attach/58cb60f5607de.pdf",
      },
      {
        label: "Department of Health: flood preparedness",
        url: "https://www.anamai.moph.go.th/th/news-anamai/44561",
      },
      {
        label: "National Weather Service: Flood safety",
        url: "https://www.weather.gov/safety/flood",
      },
    ],
  },
  {
    slug: "fire-evacuation",
    category: "hazards",
    icon: "home",
    title: "learn.library.fire.title",
    detail: "learn.library.fire.detail",
    status: "review_required",
    source: "learn.library.source.thailand_dossier",
    researchedOn: "2026-09-30",
    explanation: "learn.detail.fire.explanation",
    whyItMatters: "learn.detail.fire.why",
    scope: "learn.detail.fire.scope",
    gaps: "learn.detail.fire.gaps",
    sources: [
      {
        label: "DDPM: building fire-evacuation guidance",
        url: "https://disaster.go.th/upload/download/file_attach/dbbf840a9b17c0f03566bfce5199b90d.pdf",
      },
      {
        label: "U.S. Fire Administration: Home Fire Escape Plans",
        url: "https://www.usfa.fema.gov/prevention/home-fires/prepare-for-fire/home-fire-escape-plans/",
      },
    ],
  },
  {
    slug: "chemicals",
    category: "health",
    icon: "alert",
    title: "learn.library.chemicals.title",
    detail: "learn.library.chemicals.detail",
    status: "review_required",
    source: "learn.library.source.thailand_dossier",
    researchedOn: "2026-09-30",
    explanation: "learn.detail.chemicals.explanation",
    whyItMatters: "learn.detail.chemicals.why",
    scope: "learn.detail.chemicals.scope",
    gaps: "learn.detail.chemicals.gaps",
    sources: [
      {
        label: "Department of Health: chemical leak advice (2024-05-01)",
        url: "https://www.anamai.moph.go.th/th/news-anamai/43638",
      },
      {
        label: "CDC: Chemical emergencies",
        url: "https://www.cdc.gov/chemical-emergencies/about/index.html",
      },
    ],
  },
  {
    slug: "first-aid",
    category: "health",
    icon: "check",
    title: "learn.library.first_aid.title",
    detail: "learn.library.first_aid.detail",
    status: "review_required",
    source: "learn.library.source.thailand_dossier",
    researchedOn: "2026-09-30",
    explanation: "learn.detail.first_aid.explanation",
    whyItMatters: "learn.detail.first_aid.why",
    scope: "learn.detail.first_aid.scope",
    gaps: "learn.detail.first_aid.gaps",
    sources: [
      {
        label: "Thai Red Cross: First Aid Training Centre",
        url: "https://redcross.or.th/aboutus/department/firstaid/",
      },
      {
        label: "Thai Red Cross: first-aid manual — choking section",
        url: "https://trcyvolunteer.redcross.or.th/wp-content/uploads/2024/08/%E0%B8%84%E0%B8%B9%E0%B9%88%E0%B8%A1%E0%B8%B7%E0%B8%AD%E0%B8%9B%E0%B8%90%E0%B8%A1%E0%B8%9E%E0%B8%A2%E0%B8%B2%E0%B8%9A%E0%B8%B2%E0%B8%A5%E0%B8%AA%E0%B8%B3%E0%B8%AB%E0%B8%A3%E0%B8%B1%E0%B8%9A%E0%B9%80%E0%B8%A2%E0%B8%B2%E0%B8%A7%E0%B8%8A%E0%B8%99.pdf",
      },
    ],
  },
  {
    slug: "public-safety",
    category: "public",
    icon: "shield",
    title: "learn.library.public_safety.title",
    detail: "learn.library.public_safety.detail",
    status: "review_required",
    source: "learn.library.source.thailand_dossier",
    researchedOn: "2026-09-30",
    explanation: "learn.detail.public_safety.explanation",
    whyItMatters: "learn.detail.public_safety.why",
    scope: "learn.detail.public_safety.scope",
    gaps: "learn.detail.public_safety.gaps",
    sources: [
      {
        label: "Royal Thai Police information: public active-threat survival",
        url: "https://samutprakan.police.go.th/index.php/en/h-news/news/316-active-shooter",
      },
      {
        label: "CISA: Active Shooter Preparedness",
        url: "https://www.cisa.gov/active-shooter-preparedness",
      },
    ],
  },
  {
    slug: "family-preparedness",
    category: "preparedness",
    icon: "family",
    title: "learn.library.family.title",
    detail: "learn.library.family.detail",
    status: "draft",
    source: "learn.library.source.local_tools",
    researchedOn: "2026-10-30",
    explanation: "learn.detail.family.explanation",
    whyItMatters: "learn.detail.family.why",
    scope: "learn.detail.family.scope",
    gaps: "learn.detail.family.gaps",
    sources: [
      { label: "SafeSphere Family Plan (on-device feature)", url: "/family-plan" },
      {
        label: "Ready.gov: Family Emergency Communication Plan",
        url: "https://www.ready.gov/sites/default/files/2020-03/family-emergency-communication-planning-document.pdf",
      },
      {
        label: "ICRC: Rules of war — protection of civilians",
        url: "https://www.icrc.org/en/document/FAQ-rules-of-war-ihl",
      },
      {
        label: "DDPM: Citizen handbook — emergency survival kit",
        url: "https://www.disaster.go.th/upload/download/file_attach/58cb60f5607de.pdf",
      },
      {
        label: "UK Government: Be Ready emergency checklist",
        url: "https://beready.campaign.gov.uk/be-ready-for-emergencies/",
      },
    ],
  },
  {
    slug: "medicines",
    category: "health",
    icon: "check",
    title: "learn.library.medicines.title",
    detail: "learn.library.medicines.detail",
    status: "research_only",
    source: "learn.library.source.thai_fda",
    researchedOn: "2026-10-18",
    explanation: "learn.detail.medicines.explanation",
    whyItMatters: "learn.detail.medicines.why",
    scope: "learn.detail.medicines.scope",
    gaps: "learn.detail.medicines.gaps",
    sources: [
      {
        label: "Thai FDA: Read the medicine label and follow directions",
        url: "https://ndi.fda.moph.go.th/drug_use/detail/68",
      },
      {
        label: "Thai FDA: medicine use and storage information",
        url: "https://dis.fda.moph.go.th/detail-infoGraphic?id=2269",
      },
      {
        label: "FDA: Grapefruit Juice and Some Drugs Don't Mix",
        url: "https://www.fda.gov/consumers/consumer-updates/grapefruit-juice-and-some-drugs-dont-mix",
      },
      {
        label: "FDA: Drug Interactions: What You Should Know",
        url: "https://www.fda.gov/drugs/resources-drugs/drug-interactions-what-you-should-know",
      },
    ],
  },
  {
    slug: "food-labels",
    category: "health",
    icon: "book",
    title: "learn.library.food_labels.title",
    detail: "learn.library.food_labels.detail",
    status: "research_only",
    source: "learn.library.source.thai_fda",
    researchedOn: "2026-10-18",
    explanation: "learn.detail.food_labels.explanation",
    whyItMatters: "learn.detail.food_labels.why",
    scope: "learn.detail.food_labels.scope",
    gaps: "learn.detail.food_labels.gaps",
    sources: [
      {
        label: "Thai FDA Food Division: label requirements and examples",
        url: "https://food.fda.moph.go.th/for-entrepreneurs/test-labeling-and-examples",
      },
      {
        label: "Thai FDA Food Division: allergen-label FAQs",
        url: "https://food.fda.moph.go.th/faqs/category/faq-label-container?page=7",
      },
      {
        label: "CDC: About Sodium and Health",
        url: "https://www.cdc.gov/salt/about/",
      },
      {
        label: "NIDDK: Healthy Eating for Adults with Chronic Kidney Disease",
        url: "https://www.niddk.nih.gov/health-information/kidney-disease/chronic-kidney-disease-ckd/healthy-eating-adults-chronic-kidney-disease",
      },
    ],
  },
];
